import { SUPPORTED_TENANTS, type TenantId } from './constants'
import RtificialLogo from '@/assets/branding/logos/rtificial.png'

const APPLICA_GURU_SUFFIX = 'applica.guru'
const DEFAULT_TENANT: TenantId = 'persona'
const LOCAL_HOSTS = ['localhost', '127.0.0.1', '0.0.0.0']
const TENANT_QUERY_PARAM = 'tenant'
const TENANT_STORAGE_KEY = 'cp:tenant'

const TENANT_ALIASES: Record<string, TenantId> = {
  rtficial: 'rtificial',
}

type ColorThemeId = 'persona' | 'petrol' | 'cobalt' | 'plum' | 'indigo' | 'noir' | 'oro' | 'rtificial'

interface TenantBranding {
  name: string
  appName: string
  appCopy: string
  authCopy: string
  logoExtension: string | null
  colorTheme: ColorThemeId
  googleOAuthEnabled: boolean
}

const BRANDING_BY_TENANT: Record<TenantId, TenantBranding> = {
  persona: {
    name: 'Persona',
    appName: 'Persona',
    appCopy: 'Applica Software Guru for Persona',
    authCopy: 'Applica Software Guru for Persona',
    logoExtension: null,
    colorTheme: 'persona',
    googleOAuthEnabled: true,
  },
  rtificial: {
    name: 'Rtificial',
    appName: 'Rtificial',
    appCopy: '@rtificial - More than real',
    authCopy: '@rtificial - More than real',
    logoExtension: 'png',
    colorTheme: 'rtificial',
    googleOAuthEnabled: false,
  },
}

const TENANT_LOGOS: Record<TenantId, string> = {
  persona: '',
  rtificial: RtificialLogo,
}

function getLogoCandidates(tenant: TenantId): string[] {
  const logo = TENANT_LOGOS[tenant]
  if (logo) return [logo]
  return []
}

function normalizeTenant(raw: string): TenantId | null {
  const lower = raw.toLowerCase()
  if (TENANT_ALIASES[lower]) return TENANT_ALIASES[lower]
  if (SUPPORTED_TENANTS.includes(lower as TenantId)) return lower as TenantId
  return null
}

function isLocalHost(hostname: string): boolean {
  return LOCAL_HOSTS.includes(hostname)
}

export function resolveTenant(): TenantId {
  const hostname = window.location.hostname

  if (isLocalHost(hostname)) {
    const params = new URLSearchParams(window.location.search)
    const queryTenant = params.get(TENANT_QUERY_PARAM)
    if (queryTenant) {
      const resolved = normalizeTenant(queryTenant)
      if (resolved) {
        localStorage.setItem(TENANT_STORAGE_KEY, resolved)
        return resolved
      }
    }

    const stored = localStorage.getItem(TENANT_STORAGE_KEY)
    if (stored) {
      const resolved = normalizeTenant(stored)
      if (resolved) return resolved
    }
  }

  if (hostname.endsWith(`.${APPLICA_GURU_SUFFIX}`)) {
    const parts = hostname.split('.')
    if (parts.length >= 3) {
      const subdomain = parts[0]
      const resolved = normalizeTenant(subdomain)
      if (resolved) return resolved
    }
  }

  return DEFAULT_TENANT
}

export type { ColorThemeId }
export interface BrandingInfo {
  tenant: TenantId
  appName: string
  appCopy: string
  authCopy: string
  logoCandidates: string[]
  isPersonaTenant: boolean
  colorTheme: ColorThemeId
  googleOAuthEnabled: boolean
}

export function getBranding(): BrandingInfo {
  const tenant = resolveTenant()
  const branding = BRANDING_BY_TENANT[tenant]

  return {
    tenant,
    appName: branding.appName,
    appCopy: branding.appCopy,
    authCopy: branding.authCopy,
    logoCandidates: getLogoCandidates(tenant),
    isPersonaTenant: tenant === 'persona',
    colorTheme: branding.colorTheme,
    googleOAuthEnabled: branding.googleOAuthEnabled,
  }
}

export const BRANDING = getBranding()
